import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, profile, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'Comment ça marche', id: 'how-it-works' },
    { label: 'Pourquoi CotIdel', id: 'pourquoi' },
    { label: 'Tarifs', id: 'pricing' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
        <Link to="/" className="flex items-center gap-2 group cursor-pointer">
          <span className="text-xl md:text-2xl font-bold font-heading tracking-tight text-foreground-950">
            CotIdel<span className="text-accent-500">.</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/dashboard"
                className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {profile?.first_name || 'Mon compte'}
                {profile?.role === 'admin' && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-semibold uppercase tracking-wide">
                    <i className="ri-shield-star-line"></i>
                    Admin
                  </span>
                )}
              </Link>
              <Link
                to="/analysis"
                className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Analyse
              </Link>
              {profile?.role === 'admin' && (
                <Link
                  to="/admin"
                  className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                >
                  <i className="ri-shield-star-line"></i>
                  Admin
                </Link>
              )}
              <button
                onClick={signOut}
                className="text-sm font-semibold px-5 py-2.5 rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link
                to="/auth/login"
                className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Connexion
              </Link>
              <Link
                to="/app"
                className="text-sm font-semibold px-5 py-2.5 rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                COTER UN SOIN
              </Link>
            </>
          )}
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-950 cursor-pointer"
          aria-label="Menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70">
          <div className="px-4 py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-sm font-medium text-foreground-700 hover:text-primary-600 py-3 cursor-pointer text-left"
              >
                {link.label}
              </button>
            ))}
            <div className="flex flex-col gap-2 pt-2 border-t border-background-200/70">
              {user ? (
                <>
                  <Link to="/dashboard" className="text-sm font-medium text-foreground-700 hover:text-primary-600 py-3 cursor-pointer flex items-center gap-2">
                    Tableau de bord
                    {profile?.role === 'admin' && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-semibold uppercase tracking-wide">
                        <i className="ri-shield-star-line"></i>
                        Admin
                      </span>
                    )}
                  </Link>
                  <Link to="/analysis" className="text-sm font-medium text-foreground-700 hover:text-primary-600 py-3 cursor-pointer">
                    Analyse de cotation
                  </Link>
                  {profile?.role === 'admin' && (
                    <Link to="/admin" className="text-sm font-medium text-foreground-700 hover:text-primary-600 py-3 cursor-pointer flex items-center gap-1.5">
                      <i className="ri-shield-star-line"></i>
                      Administration
                    </Link>
                  )}
                  <button onClick={signOut} className="text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 px-5 py-2.5 rounded-md text-center cursor-pointer whitespace-nowrap">
                    Déconnexion
                  </button>
                </>
              ) : (
                <>
                  <Link to="/auth/login" className="text-sm font-medium text-foreground-700 hover:text-primary-600 py-3 cursor-pointer">
                    Connexion
                  </Link>
                  <Link to="/app" className="text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 px-5 py-2.5 rounded-md text-center cursor-pointer whitespace-nowrap">
                    COTER UN SOIN
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}